// src/lib/api/communication/calendar.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, sanitizeInput } from '../shared/validation'

/**
 * Get calendar events
 * Migrated from supabaseApi.js lines 2350-2382
 * @param {string} [providedCompanyId] - Optional company ID (avoids internal lookup)
 */
const _getCalendarEvents = async (providedCompanyId = null) => {
  const companyId = providedCompanyId || await getValidatedCompanyId()
  if (!companyId) {
    return createSuccessResponse([])
  }
  
  // Day-aligned lower bound: a millisecond-precise "now - 90d" made every
  // request URL unique, defeating HTTP caching and HAR replay in snapshots.
  const since = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
  since.setUTCHours(0, 0, 0, 0)

  const { data, error } = await supabase
    .from('calendar_events')
    .select('*')
    .eq('company_id', companyId)
    .gte('start_time', since.toISOString())
    .order('start_time', { ascending: true })
    .limit(500)

  if (error) {
    console.error('[communication] Error fetching calendar events:', error)
    throw error
  }
  
  // Map database fields to frontend expected format
  const mappedData = (data || []).map(event => ({
    ...event,
    start: new Date(event.start_time),
    end: new Date(event.end_time),
    // Keep original fields for backward compatibility
    start_time: event.start_time,
    end_time: event.end_time
  }))
  
  return createSuccessResponse(mappedData)
}

/**
 * Create calendar event
 * Migrated from supabaseApi.js lines 2383-2428
 */
const _createCalendarEvent = async (eventData) => {
  validateRequired(eventData, 'Event data')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  // Map the eventData to the correct calendar_events table structure
  const mappedData = {
    company_id: eventData.company_id || companyId,
    title: sanitizeInput(eventData.jobData?.title || eventData.title || 'Calendar Event'),
    description: eventData.jobData?.description ? sanitizeInput(eventData.jobData.description) : (eventData.description ? sanitizeInput(eventData.description) : ''),
    start_time: eventData.start_time || eventData.start,
    end_time: eventData.end_time || eventData.end || (eventData.start_time || eventData.start ? new Date(new Date(eventData.start_time || eventData.start).getTime() + 2 * 60 * 60 * 1000).toISOString() : null),
    all_day: eventData.all_day || false,
    job_id: eventData.job_id || null,
    user_id: eventData.user_id || eventData.employeeId || eventData.created_by || null
  }
  
  // Removed excessive logging - only log in development if needed
  if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
    console.log('[calendar] Creating calendar event with data:', mappedData)
  }
  
  const { data, error } = await supabase
    .from('calendar_events')
    .insert([mappedData])
    .select()
    .single()
    
  if (error) {
    console.error('[communication] Error creating calendar event:', error)
    console.error('[communication] Event data that failed:', mappedData)
    throw error
  }
  
  // Removed excessive logging - only log in development if needed
  if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
    console.log('[calendar] Calendar event created successfully:', data.id)
  }
  
  // Map the returned data to frontend expected format
  const mappedResponse = {
    ...data,
    start: new Date(data.start_time),
    end: new Date(data.end_time),
    // Keep original fields for backward compatibility
    start_time: data.start_time,
    end_time: data.end_time
  }
  
  return mappedResponse
}

/**
 * Update calendar event
 * Migrated from supabaseApi.js lines 2429-2481
 */
const _updateCalendarEvent = async (eventId, eventData) => {
  
  validateRequired(eventId, 'Event ID')
  validateRequired(eventData, 'Event data')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  // Map the eventData to the correct calendar_events table structure
  const mappedData = {
    company_id: companyId,
    title: eventData.jobData?.title ? sanitizeInput(eventData.jobData.title) : (eventData.title ? sanitizeInput(eventData.title) : undefined),
    description: eventData.jobData?.description ? sanitizeInput(eventData.jobData.description) : (eventData.description ? sanitizeInput(eventData.description) : undefined),
    start_time: eventData.start || eventData.start_time,
    end_time: eventData.end || eventData.end_time,
    location: eventData.location ? sanitizeInput(eventData.location) : undefined,
    created_by: eventData.employeeId || eventData.created_by
  }
  
  // Remove undefined values to avoid overwriting with null
  Object.keys(mappedData).forEach(key => {
    if (mappedData[key] === undefined) {
      delete mappedData[key]
    }
  })
  
  
  const { data, error } = await supabase
    .from('calendar_events')
    .update(mappedData)
    .eq('id', eventId)
    .select()
    .single()
    
  if (error) {
    console.error('[communication] Error updating calendar event:', error)
    throw error
  }
  
  // Map the returned data to frontend expected format
  const mappedResponse = {
    ...data,
    start: new Date(data.start_time),
    end: new Date(data.end_time),
    // Keep original fields for backward compatibility
    start_time: data.start_time,
    end_time: data.end_time
  }
  
  return mappedResponse
}

/**
 * Delete calendar event
 * Migrated from supabaseApi.js lines 2482-2502
 */
const _deleteCalendarEvent = async (eventId) => {
  
  validateRequired(eventId, 'Event ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { error } = await supabase
    .from('calendar_events')
    .delete()
    .eq('id', eventId)
    
  if (error) {
    console.error('[communication] Error deleting calendar event:', error)
    throw error
  }

  return { success: true }
}

// Export functions with error handling
export const getCalendarEvents = withErrorHandling(_getCalendarEvents, 'get calendar events', 'calendar_events')
export const createCalendarEvent = withErrorHandling(_createCalendarEvent, 'create calendar event', 'calendar_event')
export const updateCalendarEvent = withErrorHandling(_updateCalendarEvent, 'update calendar event', 'calendar_event')
export const deleteCalendarEvent = withErrorHandling(_deleteCalendarEvent, 'delete calendar event', 'calendar_event')